import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    if (!token) { toast.error("Invalid or missing reset link"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-4 py-12 bg-[#FDFBF7]">
      <Card className="w-full max-w-md p-8 border-[#EAE3D2] shadow-xl bg-white" data-testid="reset-password-card">
        <div className="flex items-center gap-3">
          <KeyRound className="w-7 h-7 text-[#6B1D2F]" />
          <h1 className="font-serif text-3xl font-semibold text-slate-900">Set a new password</h1>
        </div>
        <p className="text-sm text-slate-600 mt-2">Choose a strong password of at least 8 characters.</p>

        {!token ? (
          <div className="mt-6 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-900" data-testid="reset-invalid">
            This reset link is missing or invalid. Please request a fresh <Link to="/forgot-password" className="underline font-semibold">password reset</Link>.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="rp-new" className="text-slate-700">New password</Label>
              <Input id="rp-new" data-testid="reset-password-new" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required className="mt-1" />
            </div>
            <div>
              <Label htmlFor="rp-confirm" className="text-slate-700">Confirm new password</Label>
              <Input id="rp-confirm" data-testid="reset-password-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1" />
            </div>
            <Button type="submit" disabled={busy} data-testid="reset-password-submit" className="w-full bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full py-6">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}

        <p className="text-sm text-center text-slate-600 mt-6">
          <Link to="/login" data-testid="reset-back-to-login" className="text-[#6B1D2F] font-semibold">Back to sign in</Link>
        </p>
      </Card>
    </div>
  );
}
